// 소통방 — shared label mapping + time formatting.
// Stored Firestore category values stay as-is (자기소개 / 질문방 / 인증하기) for
// full back-compat with the legacy threads boards; these maps only handle the
// display-side rename to 자기소개 / 질문 / 인증.

// stored value → display label
export const CATEGORY_LABEL = {
  자기소개: '자기소개',
  질문방: '질문',
  인증하기: '인증',
};

// composer label → stored value
export const LABEL_TO_CATEGORY = {
  자기소개: '자기소개',
  질문: '질문방',
  인증: '인증하기',
};

// categories whose posts carry a title
export const TITLED_CATEGORIES = ['질문방', '인증하기'];

export function categoryLabel(stored) {
  return CATEGORY_LABEL[stored] || stored || '';
}

export function hasTitle(stored) {
  return TITLED_CATEGORIES.includes(stored);
}

// Korean-native relative-then-absolute. Within 14 days reads as quick
// social-feed language (방금 / N분 전 / N시간 전 / 어제 / N일 전 / 지난주);
// older posts render as a clean Korean date (5월 6일 for current year,
// 2025년 5월 6일 for older). No period-separated software metadata.
export function formatPostTime(iso) {
  if (!iso) return '';
  const then = new Date(iso);
  const now = new Date();
  const diffSec = Math.floor((now.getTime() - then.getTime()) / 1000);
  if (diffSec < 60) return '방금';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}분 전`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}시간 전`;

  const days = Math.floor(diffSec / 86400);
  if (days === 1) return '어제';
  if (days < 7) return `${days}일 전`;
  if (days < 14) return '지난주';

  const m = then.getMonth() + 1;
  const d = then.getDate();
  if (then.getFullYear() === now.getFullYear()) return `${m}월 ${d}일`;
  return `${then.getFullYear()}년 ${m}월 ${d}일`;
}

// ─── Profile foundation ─────────────────────────────────────────────────────

// Seed values for the industry dropdown. Members pick one. Korean labels only.
export const INDUSTRIES = [
  '금융',
  '테크',
  '컨설팅',
  '제조',
  '헬스케어',
  '미디어/엔터테인먼트',
  '리테일/소비재',
  '법률',
  '교육',
  '스타트업',
  '사이버/보안',
  '국제기구',
  '기타',
];

// Conversation interest tags — members pick up to 3.
export const CONVERSATION_INTERESTS = [
  '미국 본사 소통',
  '영어 발표/프레젠테이션',
  '글로벌 팀 매니지먼트',
  '해외 진출/사업 개발',
  '영어 협상',
  '영어 글쓰기/이메일',
  '미국 취업/이직',
  '영어 회의 리딩',
  '1:1 비즈니스 영어',
  '비영어권 동료와 영어 소통',
];

export const MAX_CONVERSATION_INTERESTS = 3;

// Company-visibility setting determines which of the two values (company,
// industry) actually surface in member-facing displays. Apply this rule
// consistently in every render context (post cards, comments, 새 멤버 strip,
// profile modal).
//
// Returns the trailing piece of the "[role] · [...]" line, or '' if nothing
// should follow the role.
export function visibleCompanyOrIndustry(profile) {
  if (!profile) return '';
  const v = profile.companyVisibility || 'industry_only';
  if (v === 'hidden') return '';
  if (v === 'full') return profile.company || profile.industry || '';
  return profile.industry || '';
}

// Compact secondary line under a member name. Returns '' if there's nothing
// meaningful to show (don't render the line at all in that case).
export function profileSecondaryLine(profile) {
  if (!profile?.role) return '';
  const suffix = visibleCompanyOrIndustry(profile);
  return suffix ? `${profile.role} · ${suffix}` : profile.role;
}

// Initials fallback for avatars when no avatarUrl has been uploaded.
// Korean names: first character only. Latin names: first letter of each
// word, up to two. Empty → "?".
export function initialsFor(name) {
  if (!name) return '?';
  const trimmed = String(name).trim();
  if (!trimmed) return '?';
  // CJK heuristic — if the first char is in Hangul / CJK block, just use it.
  if (/[ㄱ-힝一-鿿]/.test(trimmed[0])) return trimmed[0];
  const parts = trimmed.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0].toUpperCase()).join('');
}

// A profile is complete when all three required fields are populated.
// Optional fields (company, currentFocus, conversationInterests) don't gate.
export function isProfileComplete(profile) {
  if (!profile) return false;
  return Boolean(profile.avatarUrl && profile.role && profile.industry);
}

// Best-effort full name from a user/profile doc. Falls back gracefully.
export function fullName(profile) {
  if (!profile) return '';
  if (profile.displayName) return profile.displayName;
  return [profile.fname, profile.lname].filter(Boolean).join(' ');
}

// Best-effort first name. Prefers the explicit fname field, then falls back
// to the first word of displayName. Used where space is tight (the strip
// under-avatar label) so we don't show the full "Lee Eddie" treatment.
export function firstName(profile) {
  if (!profile) return '';
  if (profile.fname) return profile.fname;
  return (profile.displayName || '').split(' ')[0] || '';
}

// ─── 인증 (Certified) posts ─────────────────────────────────────────────────
//
// Phase 3 introduces structured 인증 posts with three fields: 상황, 표현, 코멘트.
// The legacy `message` (body) field is also populated with a formatted concat
// so any older render path still has *something* to display — purely internal
// belt-and-suspenders, no external consumers read this string today.

// Stored category value for 인증 posts (kept as the legacy literal for full
// schema back-compat). Use this constant everywhere rather than the string.
export const CERTIFIED_CATEGORY = '인증하기';

export function isCertifiedPost(thread) {
  if (!thread) return false;
  return thread.category === CERTIFIED_CATEGORY;
}

// Format the legacy `message` body for a 인증 post. Coment line is omitted
// when empty. Used at write time (in threadService.createNewThread) and any
// future edit path.
export function formatCertifiedBody({ situation, expression, comment }) {
  const lines = [];
  if (situation) lines.push(`[상황] ${situation}`);
  if (expression) lines.push(`[표현] ${expression}`);
  const trimmedComment = (comment || '').trim();
  if (trimmedComment) lines.push(`[코멘트] ${trimmedComment}`);
  return lines.join('\n');
}

/** The author snapshot a new thread carries (name/title/picture/tier/rank/
 *  avatar), built the way CommunityPanel and the library write path build
 *  it. Rank from points, as everywhere else. */
export function threadAuthor(profile, uid) {
  const u = profile || {};
  const points = Number(u.points) || 0;
  return {
    uid: uid || u.uid || null,
    name: fullName(u) || u.koreanName || '멤버',
    title: u.title || null,
    picture: u.picture || null,
    tier: u.tier || null,
    rank: points > 150 ? 1 : points > 80 ? 2 : 3,
    avatarUrl: u.avatarUrl || null,
  };
}

